import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {Subject} from 'rxjs';
import {CrudOperationEnum} from '../../../model/dto/enum/crud-operation.enum';
import {CrudBindingModeEnum} from '../../../model/dto/enum/crud-binding-mode.enum';
import {CrudFkResolutionEnum} from '../../../model/dto/enum/crud-fk-resolution.enum';
import {CrudLookupByEnum} from '../../../model/dto/enum/crud-lookup-by.enum';
import {
    CrudLookupDto,
    CrudOperationDescriptorDto
} from '../../../model/dto/api/crud-operation-descriptor.dto';
import {dtoServiceMap} from '../../../../../core/shared/dto/dto.service.map';

/**
 * Liste der global blockierten Felder. Spiegelt die Backend-Konstante
 * {@code LogicPermissionService.GLOBAL_BLOCKED_FIELDS}. Änderungen MÜSSEN
 * synchron mit dem Backend gehalten werden.
 *
 * @ticket TM-1000001726
 */
const GLOBAL_BLOCKED_FIELDS: ReadonlySet<string> = new Set<string>([
    'id',
    'tenantId',
    'createdBy',
    'modifiedBy',
    'createdDate',
    'lastModifiedDate',
    'version',
    'deleted',
    'deletedAt',
    'deletedBy'
]);

/**
 * Liste der Standard-Exports die der CRUD-Executor je nach Operation liefert.
 * Wird im Exports-Selector als Multi-Select angeboten.
 *
 * @ticket TM-1000001726
 */
const STANDARD_EXPORTS: string[] = [
    'id',
    'entity',
    'entities',
    'success',
    'affectedFields',
    'version',
    'validationErrors',
    'count'
];

/**
 * Editor-Status für eine einzelne Field-Mapping-Zeile. Wird auf das
 * persistierte Format {@code fieldName -> rawValue} serialisiert.
 *
 * @ticket TM-1000001726
 */
export interface FieldMappingRow {
    /** Voller Feldpfad (z.B. {@code firstName} oder {@code address.street}). */
    path: string;
    /** Eingabe-Modus: Variable-Referenz oder Literal-Wert. */
    sourceMode: 'variable' | 'literal';
    /** Bei {@code sourceMode === variable}: Name der Variable (ohne {@code $$}). */
    variableName: string;
    /** Bei {@code sourceMode === literal}: roher Literal-Wert. */
    literalValue: string;
    /** Pflicht-Toggle (UI-Hinweis - Validierung erfolgt im Backend). */
    mandatory: boolean;
    /** Optionaler Default falls Source leer/null ist. */
    defaultValue: string;
    /** FK-Resolution - nur für Pfade die FK-Referenzen darstellen. */
    fkResolution: CrudFkResolutionEnum | null;
}

/**
 * Dropdown-Option für Selects.
 *
 * @ticket TM-1000001726
 */
interface SelectOption<T> {
    label: string;
    value: T;
}

/**
 * TM-1000001726 Phase 3: strukturierter Editor für
 * {@code LogicLanguageEnum.crud}. Ersetzt den freien Code-Editor durch eine
 * formularbasierte UI zum Konfigurieren von INSERT/UPDATE/DELETE/READ.
 *
 * Inputs:
 *  - {@code script}    - aktuelles JSON-Script (kann leer sein).
 *  - {@code variables} - Liste der Import-Variablen die im Editor als
 *                        {@code $$varName}-Quelle wählbar sein sollen.
 *
 * Outputs:
 *  - {@code scriptChange} - emittet das serialisierte JSON bei jeder Mutation.
 *
 * @ticket TM-1000001726
 */
@Component({
    selector: 'mvs-crud-script-editor',
    templateUrl: './crud-script-editor.component.html',
    styleUrls: ['./crud-script-editor.component.scss'],
    standalone: false
})
export class CrudScriptEditorComponent implements OnInit, OnChanges, OnDestroy {

    /** Aktuelles JSON-Script. */
    @Input() script: string | null | undefined;

    /** Variablen-Namen die im Editor referenziert werden können. */
    @Input() variables: string[] = [];

    @Output() scriptChange: EventEmitter<string> = new EventEmitter<string>();

    // --- Enum-Referenzen fuer Template-Vergleiche ----------------------------
    // TM-1000001726: Enums sind implizit (numerisch); Template muss gegen
    // Enum-Werte vergleichen, nicht gegen String-Literale.
    protected readonly CrudOperationEnum = CrudOperationEnum;
    protected readonly CrudBindingModeEnum = CrudBindingModeEnum;
    protected readonly CrudFkResolutionEnum = CrudFkResolutionEnum;
    protected readonly CrudLookupByEnum = CrudLookupByEnum;

    initialized = false;

    // --- Operation -----------------------------------------------------------
    selectedOperation: CrudOperationEnum = CrudOperationEnum.INSERT;
    readonly operationOptions: SelectOption<CrudOperationEnum>[] = [
        {label: 'INSERT - Anlegen', value: CrudOperationEnum.INSERT},
        {label: 'UPDATE - Aktualisieren', value: CrudOperationEnum.UPDATE},
        {label: 'DELETE - Löschen', value: CrudOperationEnum.DELETE},
        {label: 'READ - Lesen', value: CrudOperationEnum.READ}
    ];

    // --- Entity-Type ---------------------------------------------------------
    /** Liste aller registrierten Entity-Type-Aliase. */
    entityTypeOptions: SelectOption<string>[] = [];
    selectedEntityType: string | null = null;

    // --- Binding -------------------------------------------------------------
    selectedBinding: CrudBindingModeEnum = CrudBindingModeEnum.EXPLICIT;
    readonly bindingOptions: SelectOption<CrudBindingModeEnum>[] = [
        {label: 'EXPLICIT - explizite Feld-Mappings', value: CrudBindingModeEnum.EXPLICIT},
        {label: 'BULK_DTO - Werte aus einer Variablen (DTO/Map)', value: CrudBindingModeEnum.BULK_DTO},
        {label: 'HYBRID - BULK + explizite Overrides', value: CrudBindingModeEnum.HYBRID}
    ];

    // --- Bulk ----------------------------------------------------------------
    bulkSource = '';
    /** Komma-separierte AllowList - wird im Editor als Multi-Select gepflegt. */
    bulkAllowedFields: string[] = [];

    // --- FK-Resolution -------------------------------------------------------
    selectedFkResolution: CrudFkResolutionEnum = CrudFkResolutionEnum.STRICT;
    readonly fkResolutionOptions: SelectOption<CrudFkResolutionEnum>[] = [
        {label: 'STRICT - findById, Fehler bei Miss', value: CrudFkResolutionEnum.STRICT},
        {label: 'LENIENT - findById, null bei Miss', value: CrudFkResolutionEnum.LENIENT},
        {label: 'REFERENCE_ONLY - nur ID-Referenz setzen', value: CrudFkResolutionEnum.REFERENCE_ONLY}
    ];

    // --- Lookup --------------------------------------------------------------
    lookupBy: CrudLookupByEnum = CrudLookupByEnum.ID;
    readonly lookupByOptions: SelectOption<CrudLookupByEnum>[] = [
        {label: 'ID - per Entity-ID', value: CrudLookupByEnum.ID},
        {label: 'CRITERIA - per Filter-Kriterium', value: CrudLookupByEnum.CRITERIA}
    ];
    /** Quelle für die Entity-ID (Variable oder Literal). */
    idSource = '';
    /** Kriterien-Einträge als editierbare Rows. */
    criteriaRows: { field: string; value: string }[] = [];

    // --- Optimistic-Lock & Nullsemantik --------------------------------------
    versionSource = '';
    /** UI-Hinweis - PARTIAL = unset bedeutet "nicht ändern", EXPLICIT_NULL = unset bedeutet "auf null setzen". */
    nullSemantic: 'PARTIAL' | 'EXPLICIT_NULL' = 'PARTIAL';
    readonly nullSemanticOptions: SelectOption<'PARTIAL' | 'EXPLICIT_NULL'>[] = [
        {label: 'PARTIAL - leere Felder unverändert lassen', value: 'PARTIAL'},
        {label: 'EXPLICIT_NULL - leere Felder auf null setzen', value: 'EXPLICIT_NULL'}
    ];

    // --- DELETE Optionen -----------------------------------------------------
    softDelete: 'ENTITY_DEFAULT' | 'FORCE_HARD' | 'FORCE_SOFT' = 'ENTITY_DEFAULT';
    readonly softDeleteOptions: SelectOption<'ENTITY_DEFAULT' | 'FORCE_HARD' | 'FORCE_SOFT'>[] = [
        {label: 'ENTITY_DEFAULT - Default der Entity', value: 'ENTITY_DEFAULT'},
        {label: 'FORCE_HARD - immer hard-delete', value: 'FORCE_HARD'},
        {label: 'FORCE_SOFT - immer soft-delete', value: 'FORCE_SOFT'}
    ];

    // --- READ Optionen -------------------------------------------------------
    readLimit: number | null = null;

    // --- Field-Mappings ------------------------------------------------------
    fieldMappings: FieldMappingRow[] = [];
    readonly sourceModeOptions: SelectOption<'variable' | 'literal'>[] = [
        {label: 'Variable', value: 'variable'},
        {label: 'Literal', value: 'literal'}
    ];

    // --- Exports -------------------------------------------------------------
    /** Standard-Exports als Multi-Select. */
    selectedExports: string[] = [];
    readonly exportOptions: SelectOption<string>[] = STANDARD_EXPORTS.map(e => ({label: e, value: e}));

    private destroy$: Subject<void> = new Subject<void>();

    constructor() {
    }

    ngOnInit(): void {
        this.loadEntityTypes();
        this.hydrateFromScript();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['script'] && !changes['script'].firstChange) {
            this.hydrateFromScript();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ---------------------------------------------------------------------------------------------
    // Entity-Type-Loading
    // ---------------------------------------------------------------------------------------------

    private loadEntityTypes(): void {
        // Alle registrierten Entity-Aliase aus dem dtoServiceMap-Registry holen.
        const aliases: string[] = [];
        dtoServiceMap.forEach((_value, key) => aliases.push(key));
        aliases.sort();
        this.entityTypeOptions = aliases.map(a => ({label: a, value: a}));
    }

    // ---------------------------------------------------------------------------------------------
    // Hydration / Serialization
    // ---------------------------------------------------------------------------------------------

    private hydrateFromScript(): void {
        if (!this.script || !this.script.trim()) {
            this.resetForm();
            return;
        }
        try {
            const parsed = JSON.parse(this.script) as CrudOperationDescriptorDto;
            this.applyParsedDescriptor(parsed);
        } catch {
            this.resetForm();
        }
    }

    private resetForm(): void {
        this.selectedOperation = CrudOperationEnum.INSERT;
        this.selectedEntityType = null;
        this.selectedBinding = CrudBindingModeEnum.EXPLICIT;
        this.bulkSource = '';
        this.bulkAllowedFields = [];
        this.selectedFkResolution = CrudFkResolutionEnum.STRICT;
        this.lookupBy = CrudLookupByEnum.ID;
        this.idSource = '';
        this.criteriaRows = [];
        this.versionSource = '';
        this.nullSemantic = 'PARTIAL';
        this.softDelete = 'ENTITY_DEFAULT';
        this.readLimit = null;
        this.fieldMappings = [];
        this.selectedExports = [];
    }

    private applyParsedDescriptor(desc: CrudOperationDescriptorDto | null | undefined): void {
        if (!desc || typeof desc !== 'object') {
            this.resetForm();
            return;
        }
        this.selectedOperation = this.toEnum(CrudOperationEnum, desc.operation, CrudOperationEnum.INSERT);
        this.selectedEntityType = desc.entityType ?? null;
        this.selectedBinding = this.toEnum(CrudBindingModeEnum, desc.binding, CrudBindingModeEnum.EXPLICIT);
        this.bulkSource = this.unwrapVariableRef(desc.bulkSource) ?? '';
        this.bulkAllowedFields = Array.isArray(desc.allowList) ? [...desc.allowList] : [];
        this.selectedFkResolution = this.toEnum(CrudFkResolutionEnum, desc.fkResolution, CrudFkResolutionEnum.STRICT);
        this.versionSource = this.unwrapVariableRef(desc.versionSource) ?? '';
        this.readLimit = typeof desc.limit === 'number' ? desc.limit : null;
        this.selectedExports = Array.isArray(desc.exports) ? [...desc.exports] : [];

        // Lookup
        if (desc.lookup) {
            this.lookupBy = this.toEnum(CrudLookupByEnum, desc.lookup.by, CrudLookupByEnum.ID);
            this.idSource = this.unwrapVariableRef(desc.lookup.value) ?? (desc.lookup.value ?? '');
            this.criteriaRows = [];
            if (desc.lookup.criteria) {
                for (const [k, v] of Object.entries(desc.lookup.criteria)) {
                    this.criteriaRows.push({field: k, value: this.unwrapToString(v)});
                }
            }
        } else {
            this.lookupBy = CrudLookupByEnum.ID;
            this.idSource = '';
            this.criteriaRows = [];
        }

        // FieldMappings
        this.fieldMappings = [];
        if (desc.fieldMappings) {
            for (const [path, raw] of Object.entries(desc.fieldMappings)) {
                const variableName = this.extractVariableName(raw);
                this.fieldMappings.push({
                    path,
                    sourceMode: variableName !== null ? 'variable' : 'literal',
                    variableName: variableName ?? '',
                    literalValue: variableName !== null ? '' : this.unwrapToString(raw),
                    mandatory: false,
                    defaultValue: '',
                    fkResolution: null
                });
            }
        }
    }

    /**
     * Mappt einen Wert aus dem persistierten JSON auf einen Enum-Wert. Das
     * Backend liefert Enum-Werte als Strings (z.B. {@code "INSERT"}); ein
     * frueher Frontend-Stand kann auch numerische Werte liefern. Beides
     * wird hier robust auf den TypeScript-Enum gemappt.
     *
     * @ticket TM-1000001726
     */
    private toEnum<E extends Record<string, string | number>, V extends E[keyof E]>(
        enumType: E,
        rawValue: unknown,
        fallback: V
    ): V {
        if (rawValue === null || rawValue === undefined) {
            return fallback;
        }
        if (typeof rawValue === 'string') {
            const mapped = (enumType as Record<string, unknown>)[rawValue];
            if (typeof mapped === 'number') {
                return mapped as V;
            }
        }
        if (typeof rawValue === 'number') {
            // Pruefen ob die Zahl ein gueltiger Enum-Wert ist.
            const name = (enumType as Record<number, string>)[rawValue];
            if (typeof name === 'string') {
                return rawValue as unknown as V;
            }
        }
        return fallback;
    }

    /**
     * Mappt einen Enum-Wert auf seine String-Repraesentation (Enum-Name) -
     * dieses Format erwartet das Backend in den persistierten Scripts.
     *
     * @ticket TM-1000001726
     */
    private fromEnum<E extends Record<string, string | number>>(
        enumType: E,
        value: number
    ): string {
        return (enumType as Record<number, string>)[value];
    }

    private extractVariableName(value: unknown): string | null {
        if (typeof value !== 'string') {
            return null;
        }
        const braceMatch = value.match(/^\$\$\{(.+)\}$/);
        if (braceMatch) {
            return braceMatch[1];
        }
        if (value.startsWith('$$')) {
            return value.substring(2);
        }
        return null;
    }

    private unwrapVariableRef(value: string | null | undefined): string | null {
        if (!value) {
            return null;
        }
        return this.extractVariableName(value);
    }

    private unwrapToString(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'string') {
            return value;
        }
        return String(value);
    }

    private wrapVariable(name: string): string {
        return `$\${${name}}`;
    }

    /**
     * Serialisiert den aktuellen Editor-Status in das Script-Format und emittet
     * es über {@link scriptChange}.
     */
    private emitScript(): void {
        if (!this.selectedEntityType) {
            this.scriptChange.emit('');
            return;
        }
        // Wire-Format: Backend erwartet Enum-Werte als Strings (z.B. "INSERT"),
        // nicht als Ordinalzahlen. Deshalb werden die Enums hier ueber
        // {@link fromEnum} zu Strings serialisiert.
        const descriptor: CrudOperationDescriptorDto = {
            operation: this.fromEnum(CrudOperationEnum, this.selectedOperation) as unknown as CrudOperationEnum,
            entityType: this.selectedEntityType
        };

        // Binding & Field-Mappings
        if (this.requiresFieldMappings()) {
            descriptor.binding = this.fromEnum(CrudBindingModeEnum, this.selectedBinding) as unknown as CrudBindingModeEnum;
            descriptor.fkResolution = this.fromEnum(CrudFkResolutionEnum, this.selectedFkResolution) as unknown as CrudFkResolutionEnum;
            const map = this.serializeFieldMappings();
            if (Object.keys(map).length > 0) {
                descriptor.fieldMappings = map;
            }
            if (this.selectedBinding === CrudBindingModeEnum.BULK_DTO
                || this.selectedBinding === CrudBindingModeEnum.HYBRID) {
                if (this.bulkSource) {
                    descriptor.bulkSource = this.wrapVariable(this.bulkSource);
                }
                if (this.bulkAllowedFields.length > 0) {
                    descriptor.allowList = [...this.bulkAllowedFields];
                }
            }
        }

        // Lookup
        if (this.requiresLookup()) {
            descriptor.lookup = this.serializeLookup();
        }

        // Version
        if (this.versionSource && this.selectedOperation !== CrudOperationEnum.INSERT
            && this.selectedOperation !== CrudOperationEnum.READ) {
            descriptor.versionSource = this.wrapVariable(this.versionSource);
        }

        // READ
        if (this.selectedOperation === CrudOperationEnum.READ && this.readLimit !== null) {
            descriptor.limit = this.readLimit;
        }

        // Exports
        if (this.selectedExports.length > 0) {
            descriptor.exports = [...this.selectedExports];
        }

        const json = JSON.stringify(descriptor);
        this.script = json;
        this.scriptChange.emit(json);
    }

    private serializeFieldMappings(): Record<string, unknown> {
        const map: Record<string, unknown> = {};
        for (const row of this.fieldMappings) {
            if (!row.path) {
                continue;
            }
            if (row.sourceMode === 'variable') {
                if (row.variableName) {
                    map[row.path] = this.wrapVariable(row.variableName);
                }
            } else {
                map[row.path] = row.literalValue;
            }
        }
        return map;
    }

    private serializeLookup(): CrudLookupDto {
        // Backend erwartet {@code by} als String (z.B. "ID").
        const lookup: CrudLookupDto = {
            by: this.fromEnum(CrudLookupByEnum, this.lookupBy) as unknown as CrudLookupByEnum
        };
        if (this.lookupBy === CrudLookupByEnum.ID) {
            // ID-Wert: wenn er einem Variablen-Pattern ähnelt -> als Var, sonst literal.
            if (this.idSource) {
                lookup.value = this.looksLikeVariableName(this.idSource)
                    ? this.wrapVariable(this.idSource)
                    : this.idSource;
            }
        } else {
            const criteria: Record<string, unknown> = {};
            for (const row of this.criteriaRows) {
                if (row.field) {
                    criteria[row.field] = this.looksLikeVariableName(row.value)
                        ? this.wrapVariable(row.value)
                        : row.value;
                }
            }
            lookup.criteria = criteria;
        }
        return lookup;
    }

    private looksLikeVariableName(value: string): boolean {
        // Eine einfache Heuristik: nur identifier-zeichen -> als Variable behandeln.
        return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value);
    }

    // ---------------------------------------------------------------------------------------------
    // Template-Helper / Operation-Mode-Flags
    // ---------------------------------------------------------------------------------------------

    requiresFieldMappings(): boolean {
        return this.selectedOperation === CrudOperationEnum.INSERT
            || this.selectedOperation === CrudOperationEnum.UPDATE;
    }

    requiresLookup(): boolean {
        return this.selectedOperation === CrudOperationEnum.UPDATE
            || this.selectedOperation === CrudOperationEnum.DELETE
            || this.selectedOperation === CrudOperationEnum.READ;
    }

    isUpdate(): boolean {
        return this.selectedOperation === CrudOperationEnum.UPDATE;
    }

    isDelete(): boolean {
        return this.selectedOperation === CrudOperationEnum.DELETE;
    }

    isRead(): boolean {
        return this.selectedOperation === CrudOperationEnum.READ;
    }

    isInsert(): boolean {
        return this.selectedOperation === CrudOperationEnum.INSERT;
    }

    requiresBulkSource(): boolean {
        return this.selectedBinding === CrudBindingModeEnum.BULK_DTO
            || this.selectedBinding === CrudBindingModeEnum.HYBRID;
    }

    // ---------------------------------------------------------------------------------------------
    // Warnings
    // ---------------------------------------------------------------------------------------------

    /** UPDATE ohne {@code versionSource} - Hinweis auf fehlendes Optimistic-Lock. */
    get showUpdateWithoutVersionWarning(): boolean {
        return this.isUpdate() && !this.versionSource;
    }

    /** Mapping enthaelt blockierte Felder - der Backend-Validator wuerde abblocken. */
    get blockedFieldPaths(): string[] {
        const blocked: string[] = [];
        for (const row of this.fieldMappings) {
            if (row.path && this.isPathBlocked(row.path)) {
                blocked.push(row.path);
            }
        }
        return blocked;
    }

    get showBlockedFieldsWarning(): boolean {
        return this.blockedFieldPaths.length > 0;
    }

    /** BULK_DTO/HYBRID ohne allowList - Pflichtfeld-Hinweis. */
    get showMissingAllowListWarning(): boolean {
        return this.requiresBulkSource()
            && (this.bulkAllowedFields.length === 0 || !this.bulkSource);
    }

    private isPathBlocked(path: string): boolean {
        // Wir vergleichen den führenden Pfad-Bestandteil (vor dem ersten Punkt)
        // gegen die Blocklist - so fängt {@code id} ebenso wie {@code id.something} (selten).
        const head = path.includes('.') ? path.substring(0, path.indexOf('.')) : path;
        return GLOBAL_BLOCKED_FIELDS.has(head);
    }

    /** Liefert {@code true} wenn das Feld in der GLOBAL_BLOCKED_FIELDS-Vorschlagsliste enthalten ist. */
    isFieldBlocked(field: string): boolean {
        return GLOBAL_BLOCKED_FIELDS.has(field);
    }

    // ---------------------------------------------------------------------------------------------
    // Event-Handler
    // ---------------------------------------------------------------------------------------------

    onOperationChange(operation: CrudOperationEnum): void {
        this.selectedOperation = operation;
        // Beim Operation-Wechsel die nicht-mehr-relevanten Felder zurücksetzen.
        if (operation === CrudOperationEnum.INSERT) {
            this.idSource = '';
            this.versionSource = '';
            this.criteriaRows = [];
        }
        if (operation === CrudOperationEnum.DELETE) {
            this.fieldMappings = [];
            this.bulkSource = '';
            this.bulkAllowedFields = [];
        }
        if (operation === CrudOperationEnum.READ) {
            this.fieldMappings = [];
            this.bulkSource = '';
            this.bulkAllowedFields = [];
            this.versionSource = '';
        }
        this.emitScript();
    }

    onEntityTypeChange(entityType: string | null): void {
        this.selectedEntityType = entityType;
        this.emitScript();
    }

    onBindingChange(binding: CrudBindingModeEnum): void {
        this.selectedBinding = binding;
        this.emitScript();
    }

    onBulkSourceChange(value: string): void {
        this.bulkSource = value ?? '';
        this.emitScript();
    }

    onBulkAllowedFieldsChange(value: string[]): void {
        this.bulkAllowedFields = value ?? [];
        this.emitScript();
    }

    /** Komma-separierte Eingabe als Convenience für den Editor. */
    onBulkAllowedFieldsInput(raw: string): void {
        this.bulkAllowedFields = (raw || '')
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0);
        this.emitScript();
    }

    onFkResolutionChange(value: CrudFkResolutionEnum): void {
        this.selectedFkResolution = value;
        this.emitScript();
    }

    onLookupByChange(value: CrudLookupByEnum): void {
        this.lookupBy = value;
        this.emitScript();
    }

    onIdSourceChange(value: string): void {
        this.idSource = value ?? '';
        this.emitScript();
    }

    addCriteriaRow(): void {
        this.criteriaRows = [...this.criteriaRows, {field: '', value: ''}];
    }

    removeCriteriaRow(index: number): void {
        this.criteriaRows = this.criteriaRows.filter((_, i) => i !== index);
        this.emitScript();
    }

    onCriteriaRowChange(): void {
        this.emitScript();
    }

    onVersionSourceChange(value: string): void {
        this.versionSource = value ?? '';
        this.emitScript();
    }

    onNullSemanticChange(value: 'PARTIAL' | 'EXPLICIT_NULL'): void {
        this.nullSemantic = value;
        // Hinweis: aktuell rein UI-seitig - kein Backend-Pendant.
    }

    onSoftDeleteChange(value: 'ENTITY_DEFAULT' | 'FORCE_HARD' | 'FORCE_SOFT'): void {
        this.softDelete = value;
        // Hinweis: aktuell rein UI-seitig - kein Backend-Pendant.
    }

    onReadLimitChange(value: number | null): void {
        this.readLimit = value;
        this.emitScript();
    }

    addFieldMappingRow(): void {
        this.fieldMappings = [...this.fieldMappings, {
            path: '',
            sourceMode: 'variable',
            variableName: '',
            literalValue: '',
            mandatory: false,
            defaultValue: '',
            fkResolution: null
        }];
    }

    removeFieldMappingRow(index: number): void {
        this.fieldMappings = this.fieldMappings.filter((_, i) => i !== index);
        this.emitScript();
    }

    onFieldMappingChange(): void {
        this.emitScript();
    }

    onExportsChange(value: string[]): void {
        this.selectedExports = value ?? [];
        this.emitScript();
    }

    /**
     * Aufgabe: prüfen ob das angegebene Feld eine FK-Referenz darstellen
     * könnte. Heuristik im Frontend: Pfad endet auf {@code Id} oder
     * {@code Dto.id}. Der echte Test läuft im Backend - hier wird nur
     * die FK-Resolution-Spalte sichtbar geschaltet.
     */
    isLikelyFkPath(path: string): boolean {
        if (!path) {
            return false;
        }
        return /Id$/.test(path) || /\bid$/.test(path) || /Dto(\.[^.]+)?$/.test(path);
    }
}
